import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Shield, CheckCircle, Copy, KeyRound, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Steps in the admin sign-in machine
const STEP = {
  CREDS: 'creds',
  SETUP_SHOW: 'setup_show',       // QR + manual secret + enter code to confirm
  SETUP_RECOVERY: 'setup_recov',  // first-time: display the 10 recovery codes once
  VERIFY: 'verify',                // returning admin: 6-digit code prompt
  RECOVERY: 'recovery',            // returning admin: use a recovery code
  FORGOT_2FA_SENT: 'forgot_2fa_sent', // email sent for "lost authenticator" reset
  FORGOT: 'forgot',
  FORGOT_SENT: 'forgot_sent',
};

const AdminLogin = () => {
  const navigate = useNavigate();

  // Step machine
  const [step, setStep] = useState(STEP.CREDS);

  // Credentials
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // MFA state (set after password OK)
  const [mfaToken, setMfaToken] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [otp, setOtp] = useState('');
  const [recoveryInput, setRecoveryInput] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);

  // Recovery codes (shown once after first-time setup)
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [acknowledgedCodes, setAcknowledgedCodes] = useState(false);
  const pendingFinalRef = useRef(null);

  useEffect(() => {
    // Defensive: wipe ANY stale flags from old code paths (incl. the old LoginModal bug)
    // before we start the new flow. Anything that's actually valid will be re-issued
    // by the backend after a successful 2FA challenge.
    try {
      const tok = localStorage.getItem('bcs_session_token');
      if (tok === 'undefined' || tok === 'null' || tok === '') {
        localStorage.removeItem('bcs_session_token');
      }
      // 'user' + 'isAuthenticated' get set by LoginModal pre-2FA; clear them on mount
      // so they can't trick downstream code into thinking the user is signed in.
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
    } catch (_) { /* ignore */ }

    // ─── Handle 2FA reset link from email ───
    // If URL has ?reset_2fa=<token>, consume it once and tell the user 2FA has been reset.
    try {
      const params = new URLSearchParams(window.location.search);
      const resetToken = params.get('reset_2fa');
      if (resetToken) {
        // Strip the token from the URL so it can't be replayed on reload
        window.history.replaceState({}, '', '/admin-login');
        fetch(`${API_URL}/api/auth/2fa/confirm-reset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ reset_token: resetToken }),
        })
          .then(async (r) => {
            const d = await r.json().catch(() => ({}));
            if (r.ok) {
              toast.success('2FA has been reset. Please sign in to set it up again.');
            } else {
              toast.error(d.detail || 'Reset link is invalid or expired');
            }
          })
          .catch(() => toast.error('Could not contact server'));
      }
    } catch (_) { /* ignore */ }

    checkExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkExistingSession = async () => {
    try {
      const savedToken = localStorage.getItem('bcs_session_token');
      const headers = {};
      if (savedToken) headers['Authorization'] = `Bearer ${savedToken}`;
      const r = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include', headers });
      if (r.ok) {
        const u = await r.json();
        if (u.email === 'jasonkraft@bcs-us.com') navigate('/admin');
      }
    } catch (_) { /* not logged in */ }
  };

  // ───────────────── Step: credentials ─────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please enter email and password'); return; }
    setIsSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, remember_me: true }),
      });
      const data = await r.json();

      if (!r.ok) { toast.error(data.detail || 'Login failed'); return; }

      // Path A: 2FA challenge required
      if (data.requires_2fa) {
        setMfaToken(data.mfa_token);
        if (data.setup_required) {
          await initSetup(data.mfa_token);
        } else {
          setStep(STEP.VERIFY);
        }
        return;
      }

      // Path B: trusted device / non-admin direct login
      finalizeSession(data);
    } catch (_) {
      toast.error('Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const finalizeSession = (data) => {
    if (data?.user?.email === 'jasonkraft@bcs-us.com') {
      if (data.session_token) localStorage.setItem('bcs_session_token', data.session_token);
      localStorage.setItem('isAuthenticated', 'true');
      toast.success('Welcome back!');
      navigate('/admin');
    } else {
      toast.error('Admin access denied');
    }
  };

  // ───────────────── Step: first-time setup ─────────────────
  const initSetup = async (token) => {
    try {
      const r = await fetch(`${API_URL}/api/auth/2fa/setup-init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mfa_token: token }),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Could not start 2FA setup'); return; }
      setQrCode(data.qr_code);
      setManualSecret(data.secret);
      setStep(STEP.SETUP_SHOW);
    } catch (_) {
      toast.error('Could not start 2FA setup');
    }
  };

  const handleSetupVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error('Enter the 6-digit code from your authenticator app'); return; }
    setIsSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/2fa/setup-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mfa_token: mfaToken, code: otp }),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Invalid code'); return; }

      // Save the codes locally; we don't enter the dashboard until user confirms they saved them.
      setRecoveryCodes(data.recovery_codes || []);
      pendingFinalRef.current = data;
      setStep(STEP.SETUP_RECOVERY);
    } catch (_) {
      toast.error('Verification failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ───────────────── Step: verify (returning) ─────────────────
  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error('Enter the 6-digit code from your authenticator app'); return; }
    setIsSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mfa_token: mfaToken, code: otp, remember_device: rememberDevice }),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Invalid code'); return; }
      finalizeSession(data);
    } catch (_) {
      toast.error('Verification failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ───────────────── Step: recovery code ─────────────────
  const handleRecovery = async (e) => {
    e.preventDefault();
    if (!recoveryInput.trim()) { toast.error('Enter a recovery code'); return; }
    setIsSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/2fa/recovery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mfa_token: mfaToken, recovery_code: recoveryInput.trim(), remember_device: rememberDevice }),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Invalid recovery code'); return; }
      toast.success(`Signed in. ${data.recovery_codes_remaining ?? 0} recovery codes remaining.`);
      finalizeSession(data);
    } catch (_) {
      toast.error('Recovery sign-in failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ───────────────── "Lost authenticator" recovery flow ─────────────────
  // Click "Lost access to authenticator?" on the verify screen → POSTs to /forgot
  // which (a) validates the mfa_token (i.e. password was correct) and (b) emails
  // a one-time signed link to the admin's address. Clicking the link wipes 2FA.
  const handleForgot2FA = async () => {
    if (!mfaToken) {
      // Caller hasn't run /login yet — bounce back to creds
      setStep(STEP.CREDS);
      toast.error('Please sign in with your password first');
      return;
    }
    setIsSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/2fa/forgot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mfa_token: mfaToken }),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Could not send reset email'); return; }
      setStep(STEP.FORGOT_2FA_SENT);
    } catch (_) {
      toast.error('Could not send reset email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      toast.success('Recovery codes copied');
    } catch (_) {
      toast.error('Could not copy — please write them down');
    }
  };

  const downloadRecoveryCodes = () => {
    const text = `BCS Admin Recovery Codes\nGenerated: ${new Date().toISOString()}\n\n${recoveryCodes.join('\n')}\n\nEach code can only be used once. Keep them safe.\n`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bcs-admin-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const continueAfterRecoveryView = () => {
    if (!acknowledgedCodes) {
      toast.error('Please confirm you saved your recovery codes');
      return;
    }
    if (pendingFinalRef.current) finalizeSession(pendingFinalRef.current);
  };

  // ───────────────── Forgot password ─────────────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setIsSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (r.ok) { setStep(STEP.FORGOT_SENT); toast.success('Check your email for a reset link'); }
      else { const d = await r.json(); toast.error(d.detail || 'Something went wrong'); }
    } catch { toast.error('Failed to send reset email'); }
    finally { setIsSubmitting(false); }
  };

  // ───────────────── Render ─────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8" data-testid="admin-login-card">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Shield className="w-8 h-8 text-blue-900" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Portal</h1>
            <p className="text-gray-500 mt-1">BCS Customer Portal Administration</p>
          </div>

          {/* ── CREDS ── */}
          {step === STEP.CREDS && (
            <form onSubmit={handleLogin} className="space-y-5" data-testid="admin-login-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input id="email" type="email" placeholder="admin@bcs-us.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} required className="h-12" data-testid="admin-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
                    value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12 pr-10"
                    data-testid="admin-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-lg"
                disabled={isSubmitting} data-testid="admin-signin-btn">
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => setStep(STEP.FORGOT)}
                  className="text-sm text-blue-700 hover:text-blue-900 font-medium" data-testid="admin-forgot-link">
                  Forgot Password?
                </button>
              </div>
            </form>
          )}

          {/* ── FIRST-TIME SETUP: show QR + ask for code ── */}
          {step === STEP.SETUP_SHOW && (
            <div className="space-y-5" data-testid="admin-2fa-setup">
              <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-sm">
                <strong>Set up Two-Factor Authentication.</strong> Scan the QR code with Google Authenticator, Authy, or 1Password — then enter the 6-digit code to finish.
              </div>
              <div className="flex justify-center">
                {qrCode ? (
                  <img src={qrCode} alt="2FA QR code" className="w-48 h-48 border rounded-lg" data-testid="admin-2fa-qr" />
                ) : (
                  <div className="w-48 h-48 bg-slate-100 rounded-lg animate-pulse" />
                )}
              </div>
              <div>
                <Label className="text-xs text-slate-500">Can't scan? Enter this key manually:</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 px-3 py-2 bg-slate-100 rounded text-sm font-mono break-all" data-testid="admin-2fa-secret">
                    {manualSecret}
                  </code>
                  <button type="button"
                    onClick={() => { navigator.clipboard.writeText(manualSecret); toast.success('Secret copied'); }}
                    className="p-2 text-slate-500 hover:text-slate-900" title="Copy secret">
                    <Copy size={16} />
                  </button>
                </div>
              </div>
              <form onSubmit={handleSetupVerify} className="space-y-3">
                <Label htmlFor="otp-setup">Verification code</Label>
                <Input id="otp-setup" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                  placeholder="123456" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className="h-12 text-center text-2xl tracking-widest font-mono" data-testid="admin-2fa-setup-code" />
                <Button type="submit" className="w-full h-12 bg-blue-900 hover:bg-blue-800"
                  disabled={isSubmitting || otp.length !== 6} data-testid="admin-2fa-setup-submit">
                  {isSubmitting ? 'Verifying...' : 'Verify & Continue'}
                </Button>
              </form>
            </div>
          )}

          {/* ── RECOVERY CODES VIEW (one-time) ── */}
          {step === STEP.SETUP_RECOVERY && (
            <div className="space-y-4" data-testid="admin-2fa-recovery-view">
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg p-3 text-sm">
                <strong>2FA is on.</strong> Save these recovery codes somewhere safe (password manager, printed copy). Each can be used <em>once</em> if you lose your phone. They are shown <em>only this one time</em>.
              </div>
              <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 border rounded-lg font-mono text-sm">
                {recoveryCodes.map((c, idx) => (
                  <div key={c} className="px-2 py-1 bg-white rounded border" data-testid={`recovery-code-${idx}`}>{c}</div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={copyRecoveryCodes} data-testid="copy-recovery-codes">
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
                <Button type="button" variant="outline" className="flex-1" onClick={downloadRecoveryCodes} data-testid="download-recovery-codes">
                  Download .txt
                </Button>
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={acknowledgedCodes} onChange={(e) => setAcknowledgedCodes(e.target.checked)}
                  className="mt-1" data-testid="ack-recovery-codes" />
                <span>I've saved these codes in a safe place.</span>
              </label>
              <Button type="button" className="w-full h-12 bg-blue-900 hover:bg-blue-800"
                disabled={!acknowledgedCodes} onClick={continueAfterRecoveryView} data-testid="continue-to-admin-btn">
                Continue to Admin Portal
              </Button>
            </div>
          )}

          {/* ── VERIFY (returning admin) ── */}
          {step === STEP.VERIFY && (
            <form onSubmit={handleVerify} className="space-y-4" data-testid="admin-2fa-verify">
              <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-lg p-3 text-sm">
                Enter the 6-digit code from your authenticator app.
              </div>
              <div className="space-y-2">
                <Label htmlFor="otp-verify">6-digit code</Label>
                <Input id="otp-verify" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                  placeholder="123456" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  autoFocus className="h-12 text-center text-2xl tracking-widest font-mono"
                  data-testid="admin-2fa-verify-code" />
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)}
                  className="mt-1" data-testid="remember-device-checkbox" />
                <span>Remember this device for 30 days (skip the code on this browser).</span>
              </label>
              <Button type="submit" className="w-full h-12 bg-blue-900 hover:bg-blue-800"
                disabled={isSubmitting || otp.length !== 6} data-testid="admin-2fa-verify-submit">
                {isSubmitting ? 'Verifying...' : 'Verify & Sign In'}
              </Button>
              <div className="flex justify-between text-sm">
                <button type="button" onClick={() => { setStep(STEP.CREDS); setOtp(''); }}
                  className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-to-creds">
                  <ArrowLeft size={14} /> Back
                </button>
                <button type="button" onClick={() => setStep(STEP.RECOVERY)}
                  className="text-blue-700 hover:text-blue-900 inline-flex items-center gap-1" data-testid="use-recovery-link">
                  <KeyRound size={14} /> Use a recovery code
                </button>
              </div>
              <div className="text-center text-sm border-t border-slate-100 pt-3">
                <button type="button" onClick={handleForgot2FA} disabled={isSubmitting}
                  className="text-slate-500 hover:text-blue-900 underline-offset-2 hover:underline disabled:opacity-50"
                  data-testid="lost-authenticator-link">
                  Lost access to your authenticator?
                </button>
              </div>
            </form>
          )}

          {/* ── FORGOT 2FA: email sent confirmation ── */}
          {step === STEP.FORGOT_2FA_SENT && (
            <div className="text-center py-4 space-y-3" data-testid="forgot-2fa-sent">
              <CheckCircle className="mx-auto text-emerald-500" size={44} />
              <h3 className="font-semibold text-slate-900">Check your email</h3>
              <p className="text-sm text-gray-600">
                If an account exists for <strong>{email}</strong>, we've sent a one-time link to reset your two-factor authentication.
              </p>
              <p className="text-xs text-gray-500">
                The link expires in 1 hour. After you click it, you'll be redirected back here to set up 2FA fresh on your next login.
              </p>
              <button type="button" onClick={() => { setStep(STEP.CREDS); setMfaToken(''); setOtp(''); }}
                className="text-sm text-blue-700 hover:text-blue-900 font-medium"
                data-testid="back-to-signin-after-forgot">
                Back to Sign In
              </button>
            </div>
          )}

          {/* ── RECOVERY CODE LOGIN ── */}
          {step === STEP.RECOVERY && (
            <form onSubmit={handleRecovery} className="space-y-4" data-testid="admin-2fa-recovery-form">
              <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-sm">
                Enter one of your single-use recovery codes (format <code>XXXX-XXXX-XXXX</code>). It will be invalidated after use.
              </div>
              <div className="space-y-2">
                <Label htmlFor="recovery">Recovery code</Label>
                <Input id="recovery" placeholder="XXXX-XXXX-XXXX" value={recoveryInput}
                  onChange={(e) => setRecoveryInput(e.target.value.toUpperCase())}
                  autoFocus className="h-12 text-center text-lg tracking-widest font-mono uppercase"
                  data-testid="admin-2fa-recovery-input" />
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)}
                  className="mt-1" />
                <span>Remember this device for 30 days.</span>
              </label>
              <Button type="submit" className="w-full h-12 bg-blue-900 hover:bg-blue-800"
                disabled={isSubmitting} data-testid="admin-2fa-recovery-submit">
                {isSubmitting ? 'Verifying...' : 'Sign in with recovery code'}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => setStep(STEP.VERIFY)}
                  className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                  <ArrowLeft size={14} /> Back to code entry
                </button>
              </div>
            </form>
          )}

          {/* ── FORGOT ── */}
          {step === STEP.FORGOT && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email Address</Label>
                <Input id="forgot-email" type="email" placeholder="admin@bcs-us.com" value={email}
                  onChange={e => setEmail(e.target.value)} required className="h-12" data-testid="admin-forgot-email" />
              </div>
              <Button type="submit" className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-lg"
                disabled={isSubmitting} data-testid="admin-forgot-submit">
                {isSubmitting ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => setStep(STEP.CREDS)}
                  className="text-sm text-gray-500 hover:text-blue-900">Back to Sign In</button>
              </div>
            </form>
          )}
          {step === STEP.FORGOT_SENT && (
            <div className="text-center py-4">
              <CheckCircle className="mx-auto mb-3 text-emerald-500" size={40} />
              <h3 className="font-semibold text-slate-900 mb-2">Check Your Email</h3>
              <p className="text-sm text-gray-500 mb-4">If an account exists for <strong>{email}</strong>, we've sent a password reset link.</p>
              <button type="button" onClick={() => setStep(STEP.CREDS)}
                className="text-sm text-blue-700 hover:text-blue-900 font-medium">Back to Sign In</button>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <a href="/" className="text-sm text-gray-500 hover:text-blue-900">← Back to BCS Website</a>
          </div>
        </div>

        {/* Branding */}
        <div className="text-center mt-6">
          <img
            src="https://customer-assets.emergentagent.com/job_a4932d93-1a08-4aba-97f8-fc665534a903/artifacts/e6h6bxv7_BF726A6E-A369-4D1E-89C3-313F853560BC_1_105_c.jpeg"
            alt="BCS Logo"
            className="h-12 w-12 mx-auto object-cover"
            style={{ clipPath: 'circle(37% at center)' }}
          />
          <p className="text-white/60 text-sm mt-2">BCS Industrial Services</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
